import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import type { Expense } from "@shared/schema";
import { isoToYmd, todayYmd } from "@shared/dates";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// Edit an expense (Sept 2026). Posted date is shown but locked — the server
// stamps it and nobody can change it. Spent date, amount, what it was for,
// who paid and the note are editable; every change lands in the history.

const FIELD = "w-full rounded-xl bg-muted/50 px-3.5 py-3 text-[15px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function EditExpenseSheet({ expense, open, onClose, payerOptions, onSaved }: {
  expense: Expense | null;
  open: boolean;
  onClose: () => void;
  /** Who can be picked as payer: group members, or you + the friend. */
  payerOptions: { id: string; name: string }[];
  onSaved: (updated: Expense) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [spentOn, setSpentOn] = useState(todayYmd());
  const [paidById, setPaidById] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!expense) return;
    setDescription(expense.description);
    setAmount(expense.amount.toFixed(2));
    setSpentOn(isoToYmd(expense.date));
    setPaidById(expense.paidById);
    setNotes(expense.notes ?? "");
    setError("");
  }, [expense, open]);

  // Converted rows (entered in another currency) keep their amount locked.
  const foreignCurrency = !!expense?.currency && expense.originalAmount != null;

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { date: spentOn, notes };
      if (!expense?.isSettlement) { body.description = description; body.paidById = paidById; }
      if (!foreignCurrency) body.amount = Number(amount);
      return (await apiRequest("PATCH", `/api/expenses/${expense!.id}`, body)).json() as Promise<Expense>;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["/api/activity"] });
      onSaved(updated);
      toast({ title: "Expense updated" });
      onClose();
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error || msg; } catch {}
      setError(msg);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!expense?.isSettlement && !description.trim()) { setError("Add what it was for."); return; }
    const n = Number(amount);
    if (!foreignCurrency && (!Number.isFinite(n) || n <= 0)) { setError("Enter an amount above zero."); return; }
    if (!spentOn || spentOn > todayYmd()) { setError("Pick a date that isn't in the future."); return; }
    save.mutate();
  };

  if (!expense) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" className="rounded-t-[28px] max-h-[90vh] overflow-y-auto bg-sidebar border-0">
        <SheetHeader className="text-left">
          <SheetTitle className="font-serif font-normal text-[30px] leading-none">Edit {expense.isSettlement ? "settle-up" : "expense"}</SheetTitle>
        </SheetHeader>
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="edit-expense-form">
          {!expense.isSettlement && (
            <div>
              <label htmlFor="edit-desc" className="block text-xs text-muted-foreground mb-1.5 ml-0.5">What was it for</label>
              <input id="edit-desc" value={description} maxLength={200} onChange={(e) => { setDescription(e.target.value); setError(""); }} className={FIELD} />
            </div>
          )}
          <div>
            <label htmlFor="edit-amount" className="block text-xs text-muted-foreground mb-1.5 ml-0.5">Amount</label>
            <input
              id="edit-amount" type="number" inputMode="decimal" step="0.01" min="0.01"
              value={amount} disabled={foreignCurrency}
              onChange={(e) => { setAmount(e.target.value); setError(""); }}
              className={`${FIELD} font-mono disabled:opacity-60`}
            />
            {foreignCurrency && <p className="text-xs text-muted-foreground mt-1.5 ml-0.5">Entered in {expense.currency}, so the amount can't be edited. Delete it and add it again to change it.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-spent" className="block text-xs text-muted-foreground mb-1.5 ml-0.5">Spent on</label>
              <input id="edit-spent" type="date" value={spentOn} max={todayYmd()} onChange={(e) => { setSpentOn(e.target.value); setError(""); }} className={FIELD} />
            </div>
            <div>
              <span className="block text-xs text-muted-foreground mb-1.5 ml-0.5">Posted</span>
              <div className="rounded-xl bg-muted/30 px-3.5 py-3 text-[15px] text-muted-foreground flex items-center justify-between gap-2" aria-label="Posted date, can't be changed">
                <span className="truncate">{expense.createdAt ? new Date(expense.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Earlier"}</span>
                <Lock className="w-3.5 h-3.5 shrink-0" />
              </div>
            </div>
          </div>
          {!expense.isSettlement && payerOptions.length > 1 && (
            <div>
              <label htmlFor="edit-payer" className="block text-xs text-muted-foreground mb-1.5 ml-0.5">Paid by</label>
              <select id="edit-payer" value={paidById} onChange={(e) => setPaidById(e.target.value)} className={`${FIELD} appearance-none`}>
                {payerOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="edit-notes" className="block text-xs text-muted-foreground mb-1.5 ml-0.5">Note</label>
            <textarea id="edit-notes" value={notes} maxLength={500} rows={2} onChange={(e) => setNotes(e.target.value)} className={`${FIELD} resize-none`} />
          </div>
          <p className="text-xs text-muted-foreground ml-0.5">Everyone on this expense can see what changed in the history.</p>
          {error && <p className="text-[13px] text-destructive ml-0.5" role="alert">{error}</p>}
          <div className="flex gap-2 pt-1 pb-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-full bg-card text-foreground text-sm font-medium py-3">Cancel</button>
            <button type="submit" disabled={save.isPending} className="flex-1 rounded-full bg-foreground text-background text-sm font-medium py-3 disabled:opacity-50" data-testid="edit-expense-save">
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
